import { useEffect, useState } from 'react';
import { getAuthenticatedClient } from '../Utils/tokenHelper';
import { useAccount, useMsal } from '@azure/msal-react';
import { enableFrontdoor } from '../Utils/features';
import { TEXT_FILE, ZIP_FILE } from '../Constants/fileTypes';
import { MultipleBatchTypeNotSupported, StandardBatchTypeNotSupported, SomethingWentWrong } from '../Constants/batchApiErrorMsg';

const BATCH_API_BASE_URL = enableFrontdoor ? process.env.REACT_APP_PRICER_API_BASE_URL_FRONTDOOR : process.env.REACT_APP_PRICER_API_BASE_URL;

export default function useBatchApi() {
    const [error, setError] = useState();
    const { accounts, instance } = useMsal();
    const account = useAccount(accounts[0]);

    /**
     * Gets batches by User ID with pagination
     * @param {string} userId - The User ID to fetch batches for
     * @param {int} skip - The number of items to skip
     * @param {int} take - The number of items to take
     * @returns {Promise<Array>} - A promise that resolves to an array of batches
     */
    const getBatchByUserId = async (userId, skip, take) => {
        try {
            const response = await getAuthenticatedClient({
                url: `${BATCH_API_BASE_URL}/batches/user/${userId}`,
                account,
                msalInstance: instance,
                method: 'GET',
                params: {
                    skip: skip,
                    take: take
                }
            });
            return response.data.batches;
        } catch (error) {
            console.error('Error fetching batches by user ID:', error);
            throw new Error("Failed to fetch batches by user ID");
        }
    };

    /**
     * Gets batches by Customer ID with pagination
     * @param {string} customerId - The Customer ID to fetch batches for
     * @param {int} skip - The number of items to skip
     * @param {int} take - The number of items to take
     * @returns {Promise<Array>} - A promise that resolves to an array of batches
     */
    const getBatchByCustomerId = async (customerId, skip, take) => {
        try {
            const response = await getAuthenticatedClient({
                url: `${BATCH_API_BASE_URL}/batches/customer/${customerId}`,
                account,
                msalInstance: instance,
                method: 'GET',
                params: {
                    skip: skip,
                    take: take
                }
            });
            return response.data.batches;
        } catch (error) {
            console.error('Error fetching batches by customer ID:', error);
            throw new Error("Failed to fetch batches by customer ID");
        }
    };

    /**
     * Creates a batch and returns a new Batch ID
     * @param {file} selectedFile - The selected file from the file picker
     * @param {int} totalCHunks - The total number of chunks expected
     */
    const createBatch = async (selectedFile, totalChunks, is837Allowed) => {
        const fileType = selectedFile.type;
        let fileTypeForApi;

        if (fileType === 'text/plain') {
            fileTypeForApi = TEXT_FILE;
        } else if (fileType === 'application/zip' || fileType === 'application/x-zip-compressed') {
            fileTypeForApi = ZIP_FILE;
        } else {
            if (!is837Allowed) {
                // Handle other file types or set a default
                fileTypeForApi = 'unknown';
                return null;
            } else {
                const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
                fileTypeForApi = fileExtension;
            }
        }
        // Calculate total size in KB
        const totalSize = selectedFile.size;
        const requestBody = {
            TotalSizeBytes: totalSize,
            fileType: fileTypeForApi,
            totalChunks: totalChunks,
            originalFileName: selectedFile.name
        };

        try {
            const response = await getAuthenticatedClient({
                url: `${BATCH_API_BASE_URL}/batches`,
                account,
                body: requestBody,
                msalInstance: instance,
                method: 'POST'
            });
            return response.data.batchId;
        } catch (error) {
            console.error('Upload error:', error);
        }
        return null;
    }

    /**
     * Marks a batch as ready by calling the API endpoint.
     * @param {string} batchId - The ID of the batch to mark as ready.
     */
    const markBatchAsReady = async (batchId) => {
        const url = `${BATCH_API_BASE_URL}/batches/${batchId}/ready`;
        try {
            const response = await getAuthenticatedClient({
                url: url,
                account,
                msalInstance: instance,
                method: 'PUT'
            });
    
            if (response.data.batchId === batchId) {
                return { success: true, errorText: null };
            } else {
                return { success: false, errorText: SomethingWentWrong };
            }
            
        } catch (error) {
            console.error('Error marking batch as ready:', error.message || error);
            let errorText = error.response?.data?.Message || SomethingWentWrong;
            const lowerCaseMessage = errorText.toLowerCase();

            if (lowerCaseMessage === MultipleBatchTypeNotSupported.toLowerCase()) {
                errorText = "Invalid mix of file types: Files in a .zip file must be all .837 files. Please try a new .zip file";
            } else if (lowerCaseMessage === StandardBatchTypeNotSupported.toLowerCase()) {
                // THis is ok to assume it was a zip file because batch api is only used for zip for large files or 837 files or individual 837 files
                // i.e this error message only comes back in specific situation with zip uploads
                errorText = "Invalid file types in zip: Files in a .zip file must be all .837 files. Please try a new .zip file.";
            }

            return { success: false, errorText: errorText };
        }
    };

    return {
        createBatch,
        markBatchAsReady,
        error,
        getBatchByCustomerId,
        getBatchByUserId
    };
}
